/** A producer-declared request for one manager-executed registered read. */
export type GovernedRegisteredResultInvocationArgumentV1 = Readonly<{
  requirementId: string;
  source: 'producer_argument' | 'relation_target';
  producerRequirementId?: string;
  value?: string | number | boolean;
}>;

export type GovernedRegisteredResultInvocationV1 = Readonly<{
  schemaVersion: 'governed_registered_result_invocation_v1';
  invocationId: string;
  target: Readonly<{ intentId: string; toolName: string }>;
  arguments: readonly GovernedRegisteredResultInvocationArgumentV1[];
  sourceRefIds: readonly string[];
}>;

export function isGovernedRegisteredResultInvocationV1(value: unknown): value is GovernedRegisteredResultInvocationV1 {
  if (!record(value) || value.schemaVersion !== 'governed_registered_result_invocation_v1') return false;
  if (!nonBlank(value.invocationId) || !record(value.target)) return false;
  if (!nonBlank(value.target.intentId) || !nonBlank(value.target.toolName)) return false;
  if (!Array.isArray(value.arguments) || !value.arguments.every(argument)) return false;
  if (!Array.isArray(value.sourceRefIds) || !value.sourceRefIds.every(nonBlank)) return false;
  return new Set(value.arguments.map((entry) => entry.requirementId)).size === value.arguments.length;
}

function argument(value: unknown): value is GovernedRegisteredResultInvocationArgumentV1 {
  if (!record(value) || !nonBlank(value.requirementId)) return false;
  if (value.source === 'producer_argument') return nonBlank(value.producerRequirementId) && value.value === undefined;
  return value.source === 'relation_target' && value.producerRequirementId === undefined && scalar(value.value);
}

function scalar(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
